import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ExpertProductApi from "../../../api/ExpertProductApi";
import PageNavigation from "../../pageNavigation/PageNavigation";
import ExpertProductAside from "./ExpertProductAside";
import ExpertProductListItem from "./ExpertProductListItem";

const PAGE_SIZE = 20;

const getInitialCategory = (target, categories = []) => {
    return categories.filter((c) => c.value === (target ? target : ""))[0]?.value;
};

const getInitialSubCategory = (target, categories = [], parentValue = "") => {
    if (!target) return "";
    const parent = categories.find((c) => c.value === parentValue);
    return parent?.children?.find((child) => child.value === target)?.value || "";
};

const getCategoryLabel = (categories, categoryValue, subCategoryValue = "") => {
    if (subCategoryValue) {
        const parent = categories.find((c) => c.value === categoryValue);
        const sub = parent?.children?.find((child) => child.value === subCategoryValue);
        if (sub) return sub.name;
    }
    return categories.find((c) => c.value === categoryValue)?.name || "전체";
};

const ExpertProductList = (props) => {
    const {
        title,
        categories = [],
        useSearch = true,
        useFilter = true,
        expert_product_owner_id = "",
        children,
        isFavoriteOnly = false,
        showAside = true,
    } = props;
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedCategory, setSelectedCategory] = useState(
        getInitialCategory(searchParams.get("category"), categories)
    );
    const [selectedSubCategory, setSelectedSubCategory] = useState(
        getInitialSubCategory(
            searchParams.get("subCategory"),
            categories,
            getInitialCategory(searchParams.get("category"), categories)
        )
    );
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [products, setProducts] = useState([]);
    const [isSearch, setIsSearch] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState("");

    const getFilterCategory = () => selectedSubCategory || selectedCategory;

    const getExpertProducts = async (opts = {}) => {
        const {
            newSearchKeyword = searchKeyword,
            newPage = page,
            newCategory = getFilterCategory(),
        } = opts;
        const rslt = await ExpertProductApi.getExpertProducts({
            searchKeyword: newSearchKeyword,
            page: newPage,
            cnt: PAGE_SIZE,
            expert_product_owner_id,
            isMine: false,
            status: "A",
            isFavoriteOnly,
            category: newCategory,
        });
        setProducts(rslt.products);
        setIsSearch((newSearchKeyword || "").trim().length > 0);
        getExpertProductsTotalCount({ newSearchKeyword });
    };

    const getExpertProductsTotalCount = async (opts = {}) => {
        const {
            newSearchKeyword = searchKeyword,
            newStatus = "A",
            newCategory = getFilterCategory(),
        } = opts;
        const rslt = await ExpertProductApi.getExpertProductsTotalCount({
            searchKeyword: newSearchKeyword,
            status: newStatus,
            isFavoriteOnly,
            category: newCategory,
        });
        setTotalCount((rslt && (rslt.totalCnt ?? rslt.total_count)) || 0);
    };

    const handleCategorySelect = (categoryValue, subCategoryValue = "") => {
        setSearchKeyword("");
        setPage(1);
        setSelectedCategory(categoryValue);
        setSelectedSubCategory(subCategoryValue);

        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (categoryValue) {
                next.set("category", categoryValue);
            } else {
                next.delete("category");
            }
            if (subCategoryValue) {
                next.set("subCategory", subCategoryValue);
            } else {
                next.delete("subCategory");
            }
            return next;
        });
    };

    const breadcrumbLabel = getCategoryLabel(
        categories,
        selectedCategory,
        selectedSubCategory
    );
    const breadcrumbParentLabel =
        selectedSubCategory &&
        categories.find((c) => c.value === selectedCategory)?.name;

    const handleSearchKeywordChange = (e) => {
        setSearchKeyword(e.target.value);
    };

    const handleSearchKeywordClick = () => {
        const keyword = searchKeyword.trim();
        setPage(1);
        getExpertProducts({ newPage: 1, newSearchKeyword: keyword });
    };

    const handleSearchCloseBtn = () => {
        setSearchKeyword("");
        setPage(1);
        getExpertProducts({ newPage: 1, newSearchKeyword: "" });
    };

    const handleSearchKeywordKeyup = (e) => {
        if (e.key === "Enter") handleSearchKeywordClick();
    };

    const handlePageChange = (newPage) => {
        if (!newPage) return;
        setPage(newPage);
    };

    useEffect(() => {
        setSearchKeyword("");
        setPage(1);
        getExpertProducts({ newPage: 1, newSearchKeyword: "" });
    }, [selectedCategory, selectedSubCategory]);

    useEffect(() => {
        if (page === 1) return;
        getExpertProducts({ newPage: page, newSearchKeyword: searchKeyword });
    }, [page]);

    return (
        <div className="aside-section-wrap">
            {showAside && categories.length > 0 && (
                <div className="section-aside-area">
                    <ExpertProductAside
                        categories={categories}
                        selectedCategory={selectedCategory}
                        selectedSubCategory={selectedSubCategory}
                        onCategorySelect={handleCategorySelect}
                    />
                </div>
            )}

            <div className="section-area">
                {children}

                <div className="section-header">
                    {showAside && categories.length > 0 && (
                        <ul className="page-location pc-only">
                            <li>
                                <Link to="/expert">전문가 서비스</Link>
                            </li>
                            <li>
                                <i className="fas fa-angle-right"></i>
                            </li>
                            {selectedSubCategory && breadcrumbParentLabel ? (
                                <>
                                    <li>
                                        <span>{breadcrumbParentLabel}</span>
                                    </li>
                                    <li>
                                        <i className="fas fa-angle-right"></i>
                                    </li>
                                    <li>
                                        <span>{breadcrumbLabel}</span>
                                    </li>
                                </>
                            ) : (
                                <li>
                                    <span>{breadcrumbLabel}</span>
                                </li>
                            )}
                        </ul>
                    )}

                    {categories.length > 0 && (
                        <div className="section-category mo-only">
                            <ul>
                                {categories.map((category) => (
                                    <li
                                        key={category.value || "all"}
                                        className={
                                            category.value === selectedCategory ? "active" : ""
                                        }
                                        onClick={() => handleCategorySelect(category.value)}
                                    >
                                        {category.name}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {useSearch && (
                        <div className="section-header-right">
                            <div className="search-bar">
                                <input
                                    type="text"
                                    placeholder="검색어를 입력해주세요"
                                    className="search-url"
                                    value={searchKeyword}
                                    onChange={handleSearchKeywordChange}
                                    onKeyUp={handleSearchKeywordKeyup}
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSearchKeywordClick}
                                >
                                    <i className="icon icon-search-bold"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {title && <h1 className="section-header-title sr-only">{title}</h1>}
                </div>

                {isSearch && (
                    <div className="search-result-summary">
                        <p className="end_txt">검색결과 {totalCount}건</p>
                        <button
                            type="button"
                            className="search-result-close"
                            onClick={handleSearchCloseBtn}
                        >
                            검색닫기
                        </button>
                    </div>
                )}

                {/* {useFilter && (
                    <div className="section-sorter">
                        <p>{totalCount} 개의 서비스</p>
                        <div className="label-text" data-target-id="adTooltip">
                            <span>AD</span>
                            <i className="icon icon-tooltip"></i>
                        </div>
                    </div>
                )} */}

                <div className="section-content">
                    <div className="vertify-list-area">
                        <ul className="vertify-list">
                            {products.map((product) => (
                                <ExpertProductListItem
                                    key={product.expert_product_id}
                                    product={product}
                                    onUpdate={setProducts}
                                    isFavoriteOnly={isFavoriteOnly}
                                />
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="section-content-tool">
                    <PageNavigation
                        postType={getFilterCategory()}
                        totalCount={totalCount}
                        showCount={PAGE_SIZE}
                        onChange={handlePageChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default ExpertProductList;
export { ExpertProductListItem };
export { ExpertProductListItem as ExpertProdcutItem };
